import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableSortLabel,
  Link,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  NotificationsActive as TriggerIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { portfolioService, LastTransactionComparison as TxComparison } from '../../services/portfolioService';
import { priceTriggersService } from '../../services/priceTriggersService';

type SortKey = 'symbol' | 'last_tx_type' | 'last_tx_date' | 'last_tx_price' | 'current_price' | 'price_change_pct' | 'action_suggestion';
type SortDir = 'asc' | 'desc';

function getScreenerUrl(symbol: string): string {
  const base = symbol.replace(/\.(NS|BO)$/, '');
  return `https://www.screener.in/company/${base}/consolidated/`;
}

const LastTransactionComparisonPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<TxComparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'buys' | 'sells'>('all');
  const [filterAction, setFilterAction] = useState<'all' | 'rebuy' | 'sell' | 'hold'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('price_change_pct');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [summary, setSummary] = useState({
    total_buys: 0, total_sells: 0, potential_rebuys: 0, potential_sells: 0,
  });

  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<TxComparison | null>(null);
  const [triggerPrice, setTriggerPrice] = useState('');
  const [triggerType, setTriggerType] = useState<'higher' | 'lower'>('lower');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await portfolioService.getLastTransactionsVsCurrent();
      setData(response.transactions);
      setSummary(response.summary);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const filteredData = data
    .filter((tx) => {
      const matchesSearch =
        tx.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.stock_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType =
        filterType === 'all' ||
        (filterType === 'buys' && (tx.last_tx_type === 'BUY' || tx.last_tx_type === 'OPEN_POSITION')) ||
        (filterType === 'sells' && tx.last_tx_type === 'SELL');
      const matchesAction =
        filterAction === 'all' ||
        (filterAction === 'rebuy' && (tx.action_suggestion === 'CONSIDER_REBUY' || tx.action_suggestion === 'WATCH_FOR_REBUY')) ||
        (filterAction === 'sell' && (tx.action_suggestion === 'CONSIDER_SELL' || tx.action_suggestion === 'WATCH_FOR_SELL')) ||
        (filterAction === 'hold' && tx.action_suggestion === 'HOLD_OR_AVERAGE');
      return matchesSearch && matchesType && matchesAction;
    })
    .sort((a, b) => {
      let va: any = (a as any)[sortKey];
      let vb: any = (b as any)[sortKey];
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const getActionChip = (action: string | null) => {
    if (!action) return null;
    const cfg: Record<string, { color: 'success' | 'warning' | 'error' | 'info'; label: string }> = {
      'CONSIDER_REBUY': { color: 'success', label: 'Consider Rebuy' },
      'WATCH_FOR_REBUY': { color: 'info', label: 'Watch for Rebuy' },
      'CONSIDER_SELL': { color: 'warning', label: 'Consider Sell' },
      'WATCH_FOR_SELL': { color: 'info', label: 'Watch for Sell' },
      'HOLD_OR_AVERAGE': { color: 'error', label: 'Hold/Average' },
    };
    const c = cfg[action];
    return c ? <Chip size="small" color={c.color} label={c.label} sx={{ height: 22, fontSize: '0.75rem' }} /> : null;
  };

  const handleOpenTriggerDialog = (tx: TxComparison) => {
    setSelectedStock(tx);
    if (tx.last_tx_type === 'SELL') {
      setTriggerType('lower');
      setTriggerPrice((tx.current_price ? tx.current_price * 0.95 : tx.last_tx_price * 0.9).toFixed(2));
    } else {
      setTriggerType('higher');
      setTriggerPrice((tx.current_price ? tx.current_price * 1.1 : tx.last_tx_price * 1.15).toFixed(2));
    }
    setTriggerDialogOpen(true);
  };

  const handleCreateTrigger = async () => {
    if (!selectedStock || !triggerPrice) return;
    try {
      await priceTriggersService.createPriceTrigger({
        symbol: selectedStock.symbol,
        trigger_price: parseFloat(triggerPrice),
        trigger_type: triggerType,
      });
      toast.success(`Price trigger created for ${selectedStock.symbol}`);
      setTriggerDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create trigger');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;

  const cellSx = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' as const };
  const headCellSx = { ...cellSx, fontWeight: 600 };

  return (
    <Box px={1} py={0.5}>
      {/* Summary Cards */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        {[
          { val: summary.total_buys, label: 'Last Action: Buy', color: 'primary.main' },
          { val: summary.total_sells, label: 'Last Action: Sell', color: 'secondary.main' },
          { val: summary.potential_rebuys, label: 'Potential Rebuys', color: 'success.dark', bg: 'success.light' },
          { val: summary.potential_sells, label: 'Potential Sells', color: 'warning.dark', bg: 'warning.light' },
        ].map((s, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card sx={{ bgcolor: s.bg }}>
              <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="h5" color={s.color}>{s.val}</Typography>
                <Typography variant="caption" color={s.color || 'text.secondary'}>{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ px: 1, py: 0.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by symbol or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: '1 1 200px', '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.8rem' } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '1rem' }} /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: '0.8rem' }}>Tx Type</InputLabel>
          <Select value={filterType} label="Tx Type" onChange={(e) => setFilterType(e.target.value as any)} sx={{ height: 32, fontSize: '0.8rem' }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="buys">Buys</MenuItem>
            <MenuItem value="sells">Sells</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: '0.8rem' }}>Action</InputLabel>
          <Select value={filterAction} label="Action" onChange={(e) => setFilterAction(e.target.value as any)} sx={{ height: 32, fontSize: '0.8rem' }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="rebuy">Rebuy</MenuItem>
            <MenuItem value="sell">Sell</MenuItem>
            <MenuItem value="hold">Hold</MenuItem>
          </Select>
        </FormControl>
        <Button startIcon={<RefreshIcon />} onClick={loadData} variant="outlined" size="small" sx={{ height: 32, fontSize: '0.75rem' }}>Refresh</Button>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>
                <TableSortLabel active={sortKey === 'symbol'} direction={sortKey === 'symbol' ? sortDir : 'asc'} onClick={() => handleSort('symbol')}>Symbol</TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx} align="center">
                <TableSortLabel active={sortKey === 'last_tx_type'} direction={sortKey === 'last_tx_type' ? sortDir : 'asc'} onClick={() => handleSort('last_tx_type')}>Last Action</TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx} align="right">
                <TableSortLabel active={sortKey === 'last_tx_date'} direction={sortKey === 'last_tx_date' ? sortDir : 'asc'} onClick={() => handleSort('last_tx_date')}>Tx Date</TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx} align="right">
                <TableSortLabel active={sortKey === 'last_tx_price'} direction={sortKey === 'last_tx_price' ? sortDir : 'asc'} onClick={() => handleSort('last_tx_price')}>Tx Price</TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx} align="right">
                <TableSortLabel active={sortKey === 'current_price'} direction={sortKey === 'current_price' ? sortDir : 'asc'} onClick={() => handleSort('current_price')}>Current Price</TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx} align="right">
                <TableSortLabel active={sortKey === 'price_change_pct'} direction={sortKey === 'price_change_pct' ? sortDir : 'asc'} onClick={() => handleSort('price_change_pct')}>Change %</TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx} align="center">
                <TableSortLabel active={sortKey === 'action_suggestion'} direction={sortKey === 'action_suggestion' ? sortDir : 'asc'} onClick={() => handleSort('action_suggestion')}>Suggestion</TableSortLabel>
              </TableCell>
              <TableCell sx={headCellSx} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((tx) => (
              <TableRow key={tx.symbol} hover>
                <TableCell sx={cellSx}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Link component="button" variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(tx.symbol)}`)}>
                      {tx.symbol}
                    </Link>
                    <Tooltip title="View on Screener.in">
                      <IconButton size="small" component="a" href={getScreenerUrl(tx.symbol)} target="_blank" rel="noopener" sx={{ p: 0.25 }}>
                        <OpenInNewIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={cellSx} align="center">
                  <Chip size="small" label={tx.last_tx_type} color={tx.last_tx_type === 'SELL' ? 'secondary' : 'primary'} sx={{ height: 20, fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell sx={cellSx} align="right">{new Date(tx.last_tx_date).toLocaleDateString()}</TableCell>
                <TableCell sx={cellSx} align="right">₹{tx.last_tx_price.toLocaleString()}</TableCell>
                <TableCell sx={cellSx} align="right">{tx.current_price ? `₹${tx.current_price.toLocaleString()}` : '-'}</TableCell>
                <TableCell sx={cellSx} align="right">
                  {tx.price_change_pct !== null ? (
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.25}>
                      {tx.price_change_pct >= 0
                        ? <TrendingUpIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />
                        : <TrendingDownIcon sx={{ fontSize: '0.9rem', color: 'error.main' }} />}
                      <Typography variant="body2" color={tx.price_change_pct >= 0 ? 'success.main' : 'error.main'} sx={{ fontSize: '0.8rem' }}>
                        {tx.price_change_pct >= 0 ? '+' : ''}{tx.price_change_pct.toFixed(2)}%
                      </Typography>
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell sx={cellSx} align="center">{getActionChip(tx.action_suggestion)}</TableCell>
                <TableCell sx={cellSx} align="center">
                  <Tooltip title="Set Price Trigger">
                    <IconButton size="small" color="primary" onClick={() => handleOpenTriggerDialog(tx)} sx={{ p: 0.25 }}>
                      <TriggerIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData.length === 0 && (
        <Box textAlign="center" py={2}>
          <Typography variant="body2" color="text.secondary">No transactions found matching your filters</Typography>
        </Box>
      )}

      {/* Trigger Dialog */}
      <Dialog open={triggerDialogOpen} onClose={() => setTriggerDialogOpen(false)}>
        <DialogTitle>Create Price Trigger for {selectedStock?.symbol}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedStock && (
              <Alert severity="info" sx={{ mb: 2, py: 0 }}>
                Last {selectedStock.last_tx_type} at ₹{selectedStock.last_tx_price.toLocaleString()}
                {' · '}Current: ₹{selectedStock.current_price?.toLocaleString() || 'N/A'}
              </Alert>
            )}
            <FormControl fullWidth sx={{ mb: 2 }} size="small">
              <InputLabel>Trigger When Price Goes</InputLabel>
              <Select value={triggerType} label="Trigger When Price Goes" onChange={(e) => setTriggerType(e.target.value as 'higher' | 'lower')}>
                <MenuItem value="higher">Higher (Sell Signal)</MenuItem>
                <MenuItem value="lower">Lower (Buy Signal)</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth size="small" label="Trigger Price (₹)" type="number" value={triggerPrice} onChange={(e) => setTriggerPrice(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerDialogOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" onClick={handleCreateTrigger} startIcon={<AddIcon />} size="small">Create Trigger</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LastTransactionComparisonPage;
